import { useEffect, useMemo, useState } from "react"

import { Col, Layout, Row, Spin } from "antd"
import cls from "classname"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { Outlet, useLocation } from "react-router-dom"

import BannerAnotherProvider from "../../Components/Banners/BannerAnotherProvider"
import BannerDemoModeFixed from "../../Components/Banners/BannerDemoModeFixed"
import TitleSection from "../../Components/TitleSection/TitleSection"
import { TitleSectionProvider } from "../../Components/TitleSection/TitleSectionContext"
import WithDelay from "../../Components/WithDelay"
import { useDemoBannerVisible } from "../../hooks/useDemoBannerVisible"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import Icon from "../../Icon"
import { navActions } from "../../Redux/nav/nav.slice"
import {
  clearProviderBanner,
  hasProviderBanner as hasProviderBannerForAccount
} from "../../Utils/anotherProviderBanner"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { getMobileHeaderTitle } from "../../Utils/utils"
import Header from "../Header"
import HistoricalDataModeBanner from "../Health/VAMP/components/HistoricalDataModeBanner/HistoricalDataModeBanner"
import { useVampHistoricalDataMode } from "../Health/VAMP/hooks/useHistoricalDataMode"
import HowDidYouFindUsForm from "../HowDidYouFindUs"
import Sidebar from "../Sidebar"
import MenuComponent from "../User"
import { useFetchBusinessAccount } from "../User/hooks/useFetchBusinessAccount"
import { getBusinessAccounts, getBusinessAccountsWithRole } from "../User/redux/user.thunks"
import { pagesConfig } from "./pagesConfig"

import "./styles.less"

const routesWithBanner = [
  "/dashboard",
  "/alerts",
  "/configure",
  "/credits",
  "/team",
  "/integrations",
  "/digital-receipt",
  "/health",
  "/perks"
]

export const LayoutComponent = ({ children, demoRef }) => {
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth)
  const nav = useSelector((state) => state.nav)
  const current_business_account_id = useSelector(
    (state) => state.auth.userInfo?.current_business_account_id
  )
  const userInfo = useSelector((state) => state.auth.userInfo)
  const externalDemoMode = useExternalDemoMode()
  const demoBannerVisibleFromHook = useDemoBannerVisible()
  const [hasProviderBanner, setHasProviderBanner] = useState(
    hasProviderBannerForAccount(current_business_account_id)
  )
  const isDemoModeBlocked = !isOnboardingFinished(userInfo) && userInfo?.next_step < 8

  const pageData = pagesConfig[pathname] || {}
  const isAdmin = auth?.isAdmin
  const { isMobile, theme } = nav

  const openMobileSidebar = () =>
    dispatch(navActions.navSetProp({ key: "isShowMenu", value: true }))
  const isGettingStarted = pathname.includes("/getting_started")
  const isBannerRoute = routesWithBanner.some((route) => pathname.startsWith(route))

  useEffect(() => {
    setHasProviderBanner(hasProviderBannerForAccount(current_business_account_id))
  }, [current_business_account_id])

  // Fetch business accounts with role and business accounts on mount if user is not admin
  useEffect(() => {
    if (!!userInfo && !isAdmin) {
      dispatch(getBusinessAccountsWithRole())
      dispatch(getBusinessAccounts())
    }
  }, [])

  // Fetch business account
  useFetchBusinessAccount()

  const isDemoBannerVisible = useMemo(() => {
    if (isAdmin || hasProviderBanner || !isBannerRoute) return false

    const isDemoLike =
      auth?.userInfo?.is_demo ||
      auth?.userInfo?.is_light_mode ||
      externalDemoMode ||
      !current_business_account_id

    return demoBannerVisibleFromHook || isDemoLike
  }, [
    auth?.userInfo,
    hasProviderBanner,
    isBannerRoute,
    isAdmin,
    externalDemoMode,
    current_business_account_id,
    demoBannerVisibleFromHook
  ])

  const isAnotherProviderBannerVisible =
    hasProviderBanner && !isGettingStarted && isBannerRoute && !isAdmin

  const { isHistoricalDataModeBannerActive } = useVampHistoricalDataMode()

  const isBannerVisible =
    isDemoBannerVisible || isAnotherProviderBannerVisible || isHistoricalDataModeBannerActive

  const handleCloseProviderBanner = () => {
    clearProviderBanner(current_business_account_id)
    setHasProviderBanner(false)
  }

  if (!auth?.accessToken) {
    return <Spin fullscreen />
  }

  return (
    <TitleSectionProvider>
      <Layout
        id="global-wrap"
        className={cls("global-wrap", {
          "demo-banner-visible": isDemoBannerVisible
        })}
        style={
          isBannerVisible && !isMobile
            ? {
                marginTop: 36,
                height: "calc(100vh - 36px)"
              }
            : {}
        }
      >
        {isDemoBannerVisible && (
          <BannerDemoModeFixed
            externalDemoMode={externalDemoMode}
            isDemoModeBlocked={isDemoModeBlocked}
            isLightMode={userInfo?.is_light_mode}
          />
        )}
        {isAnotherProviderBannerVisible && (
          <BannerAnotherProvider handleCloseProviderBanner={handleCloseProviderBanner} />
        )}
        {isHistoricalDataModeBannerActive && <HistoricalDataModeBanner />}

        {isAdmin ? (
          <Header demoRef={demoRef} />
        ) : (
          <>
            <Sidebar topMargin={isBannerVisible && !isMobile ? 36 : 0} />
            {isMobile && (
              <div className={cls("mobile-header", { [theme]: theme })}>
                <div className="mobile-header_left-section">
                  <button
                    className="mobile-header_burger"
                    onClick={openMobileSidebar}
                    aria-label="Open menu"
                  >
                    <Icon type="mobile_side_bar" />
                  </button>
                  <div className="mobile-header-title">{getMobileHeaderTitle(pathname)}</div>
                </div>
                <MenuComponent type={"sidebar-bottom-menu"} showText={false} />
              </div>
            )}
          </>
        )}
        <div
          className={cls("main-container", {
            "without-border-radius": !isAdmin && !pathname.includes("settings")
          })}
        >
          {/* {auth?.userInfo && <Sidebar />} */}
          <main
            id="main-content"
            className={cls("main-content", {
              "fad-spot": pathname.slice(1).replace("/", "_") === "ad-spot",
              [pathname.slice(1).replace("/", "_")]:
                pathname.slice(1).replace("/", "_") !== "ad-spot",
              "with-title-section": !isAdmin && pageData.title,
              "with-demo-banner": isDemoBannerVisible
            })}
          >
            {!isAdmin && pageData.title && !pathname.includes("/business_settings") && (
              <TitleSection
                className={cls("title-section-wrapper", "page-header")}
                title={pageData.title}
                description={pageData.description}
                icon={pageData.icon}
                buttonText={pageData.buttonText}
                videoUrl={pageData.videoUrl}
                queryParam={pageData.queryParam}
                queryButtonText={pageData.queryButtonText}
              />
            )}
            <Row>
              <Col flex="auto">
                <Outlet />
              </Col>
            </Row>
            <WithDelay delay={3000}>
              <HowDidYouFindUsForm />
            </WithDelay>
          </main>
        </div>
      </Layout>
    </TitleSectionProvider>
  )
}

LayoutComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

export default LayoutComponent
